/**
 * CompliSun Pitch Archetypes (per /app source doc — Feb 2026)
 *
 * 7 archetypes mapped to every prospect:
 *   1A — Regulated End-Client (fintech / bank / CU / lender)
 *   1B — CompliSun Legal Corporate (PVS, Sloan, Impulse Alarm, AAA)
 *   2A — Big Law Firm Channel (Dykema, Chapman, Mavacy)
 *   2B — Solo Attorney / Consultant Channel (Brom Law)
 *   3  — Bank Channel Partner (Cross River, TAB Bank, Celtic Bank)
 *   4  — Member Association (CBM, AMBA)
 *   6  — Real Estate Fintech Adjacent (GLEP / Lenco-GLEP)
 *
 *   (Archetype 5 = LendAPI partnership — no deck. They still get a courtesy
 *    deck via Archetype 1A defaults so the deck route doesn't 404.)
 */

const ARCHETYPE_BY_SLUG = {
  // 1B — CompliSun Legal Corporate
  "pvs-chemicals":      "1B",
  "sloan":              "1B",
  "impulse-alarm":      "1B",
  "aaa":                "1B",

  // 2A — Big Law Firm Channel
  "dykema":             "2A",
  "chapman-and-cutler": "2A",
  "mavacy":             "2A",
  "brom-law":           "2A",
  "your-law-firm":      "2A",  // Generic shareable law-firm pitch

  // 2B — Solo Channel

  // 3 — Bank Channel
  "cross-river-bank":   "3",
  "tab-bank":           "3",
  "celtic-bank":        "3",

  // 4 — Member Association
  "community-bankers-of-michigan":                       "4",
  "association-of-military-banks-of-america-amba":       "4",

  // 6 — Real Estate Fintech (combined platform + legal)
  "lenco-glep":         "6",
  "glep":               "6",
};

/** Slug → archetype id (string). Defaults to 1A. */
export function getArchetype(prospect) {
  if (!prospect) return "1A";
  const slug = (prospect.slug || "").toLowerCase();
  if (ARCHETYPE_BY_SLUG[slug]) return ARCHETYPE_BY_SLUG[slug];
  // Fallback to industry hints
  const ind = (prospect.industry || "").toLowerCase();
  if (/law firm/.test(ind)) return "2A";
  if (/attorney/.test(ind)) return "2B";
  if (/association/.test(ind)) return "4";
  return "1A";
}

/** Returns the content pack for the prospect's archetype. */
export function packFor(prospect) {
  const a = getArchetype(prospect);
  return PACKS[a] || PACKS["1A"];
}

/** Whole-deck is a law-firm pitch (firm runs on CompliSun Legal). */
export function isLawFirm(prospect) {
  const a = getArchetype(prospect);
  return a === "2A" || a === "2B";
}

/** Primary pitch IS CompliSun Legal (corporate counsel, not compliance). */
export function isLegalCorporate(prospect) {
  return getArchetype(prospect) === "1B";
}

/**
 * Subset of 1B prospects where the entire deck arc leads with CompliSun Legal
 * (outside counsel) as the PRIMARY engagement, and demotes the platform/prototype
 * to a secondary "tech layer for your in-house team" offer. Slot order and
 * Guide + Build-my-Prototype copy adapt accordingly.
 */
const LEGAL_PRIMARY_SLUGS = new Set(["sloan", "pvs-chemicals", "impulse-alarm", "aaa"]);
export function isLegalPrimary(prospect) {
  if (!prospect) return false;
  return LEGAL_PRIMARY_SLUGS.has((prospect.slug || "").toLowerCase());
}

/** Combined platform + legal engagement (GLEP). */
export function isCombined(prospect) {
  return getArchetype(prospect) === "6";
}

/** Bank channel — hold pricing back from the deck. */
export function isBankChannel(prospect) {
  return getArchetype(prospect) === "3";
}

/** Should we append a CompliSun Legal bonus slide after Contact? */
export function getsLegalCrossSell(prospect) {
  if (!prospect) return false;
  const a = getArchetype(prospect);
  // 1B + 6 already lead with legal as part of the deck → no bonus needed
  return a === "6"; // GLEP combined engagement gets the legal bonus too
}

// =============================================================================
//  CONTENT PACKS PER ARCHETYPE
// =============================================================================

const COMMON_AI = {
  title: "Your team is already using AI",
  body: "ChatGPT in browser tabs. Customer data in prompts. No guardrails, no audit trail.",
};

// ---------- 1A — Regulated End-Client ----------
const A_1A = {
  kind: "1A",
  label: "Regulated End-Client",
  problemCards: [
    { title: "Lack of a compliance program kills deals",       body: "Bank partners and acquirers ask for evidence — not promises. Without a packet, the deal stalls or dies." },
    { title: "Your compliance lead is drowning",               body: "Policies, audits, calendar gaps, one more 'quick question.' Burnout is a churn risk." },
    COMMON_AI,
    { title: "Vendor management isn't a once-a-year box",       body: "Ongoing third-party monitoring isn't optional anymore — it's both a regulatory requirement and a basic best practice. OFAC re-screening, adverse media, control attestations. One-time onboarding is the START, not the finish line." },
  ],
  guidePromise: "Compliance experts doing the work alongside automation that handles the mundane — so your team can think harder about the things that actually need a human brain. Every step generates evidence as we go, so the audit trail is just a byproduct of the work — not a separate project.",
  closedSystemsBullets: [
    "Each client runs in their own AWS VPC. Customer-managed KMS keys at rest. TLS 1.2+ in transit.",
    "Claude reaches you via AWS Bedrock + VPC PrivateLink — Anthropic has ZERO access to your prompts, completions, or documents. Architectural guarantee, not a contractual promise.",
    "Role-based access controls: one user sees training; another sees NPPI. You set the gates.",
    "Audit logs hit YOUR CloudTrail in YOUR account — examiners get a clean trail, not a vendor's exhibit.",
  ],
  platformTabs: ["dashboard", "monitoring", "vendor", "training"],
  tryItModules: ["vendor", "marketing", "training"],
  proofKpi: "$280K saved Year 1 vs hiring a CCO",
  pricingAnchor: { mo: 3225, install: 5000 },
};

// ---------- 1B — CompliSun for Corporate General Counsel ----------
const A_1B = {
  kind: "1B",
  label: "Corporate General Counsel",
  corporate: true,
  problemCards: [
    { title: "Outside counsel is a black box",                 body: "Bills arrive monthly, work product lives in someone else's inbox, and you find out the scope after the fact." },
    { title: "Your institutional knowledge is invisible",      body: "The answer is often already in a file you own — but no one can find it fast enough to use it." },
    { title: "The Board wants proof, not promises",            body: "Quarterly Board reports shouldn't require a fire drill. Evidence should be one click away." },
    { title: "AI is happening with or without guardrails",     body: "Your team is already using AI tools. The question is whether your data is protected when they do." },
  ],
  problemHeadline: { lead: "The answer is probably", italic: "already in your files.", trail: "You just can't find it." },
  problemBody1: "To stay compliant, get a clean legal opinion, or take a defensible position on risk, your team has to comb through endless folders, PDFs, Excel sheets, and old email threads — and still walk away unsure what your institutional knowledge actually says. You may already have the answer. You just can't see it. So you go to outside counsel, pay for the memo, and the cycle repeats.",
  problemBody2: "CompliSun becomes the protected layer between your data and the AI tools your team wants to use anyway — so you can finally get a hold of your own business. Like having a team of executive assistants who never sleep, never leak, and never lose a document.",
  guidePromise: "If you could afford it, you'd hire a full team — compliance experts, legal specialists, operations leads, and technologists — to build the systems that would make your job easier and the company safer. CompliSun is that team, paired with automation that handles the mundane work so your people can spend their hours on the things that actually need a human brain. Evidence is captured as we go — the audit trail is a byproduct, not a separate project.",
  guideCards: [
    { title: "Legal & compliance experts", body: "Senior compliance officers, attorneys, and operational leads — doing the actual work, not just advising on it." },
    { title: "Scalable systems",           body: "Every workflow we build for you stays — yours to run after we hand off." },
    { title: "Evidence, not memos",        body: "No more Excel-and-PDF compliance. Audit-ready trails captured automatically." },
    { title: "Your compliance story",      body: "Tell your Board, your regulators, and your auditors what you do — with the proof to back it up." },
  ],
  colinBio: "Former General Counsel and Chief Compliance Officer at Rocket. Built CompliSun so the work he used to do by hand could ship as software — paired with a team you can use as much or as little as you need.",
  closedSystemsIntro: "Open AI systems learn from everything you give them — and that data often lives on someone else's servers, available to train someone else's model. Once it's out, it's out. A 'closed system' means your data stays inside your tenant, on infrastructure you control, used only for you.",
  closedSystemsBullets: [
    "Client-privileged work product stays in YOUR tenant — never in a vendor's training set.",
    "Secure document collaboration — no more email-attached drafts vanishing into someone's inbox.",
    "Role-based access at every level: GC, finance, business owners — everyone sees only what their role permits.",
    "Privilege preserved by architecture, not just policy.",
  ],
  platformBody: "Not a binder full of policies. A live system used by legal, compliance, operations, HR, finance, and any team that touches a regulated or risk-sensitive workflow. AI agents, training, phishing tests, vendor management, calendars, audit prep — all in one place, all audit-ready.",
  trainingTitle: "Role-based modules with evidence of completion.",
  trainingBlurb: "Anti-harassment, data privacy, conflicts of interest, anti-bribery & FCPA, insider trading, information security, code of conduct, contract authority. Video lessons + quizzes. Audit-ready evidence per employee.",
  platformTabs: ["dashboard", "monitoring", "vendor", "training"],
  tryItModules: ["matter", "complimind-gc", "hours"],
  tryItSubhead: "And if you want the platform too — these are the modules your in-house GC and compliance team get. Live matter status, AI that remembers what we've advised on, budget guardrails you can set.",
  tryItLabels: {},
  titleSubline: "we built CompliSun Legal PLC to be that team — your outside counsel for commercial, regulatory, IP, employment, and the work in between. And when it helps, we layer in the technology your in-house GC and compliance team can use day-to-day.",
  titlePricingPills: [
    { eyebrow: "CompliSun Legal",            value: "Hotline · Counsel · Fractional Team (20–25% off, first 12 mo)" },
    { eyebrow: "Optional Compliance Platform", value: "$1,500 / mo · or +Fractional at $3,225 / mo" },
  ],
  titlePricingFootnote: "Primary engagement is outside counsel; the platform is available if you want the tech layer for your in-house team.",
  howItWorksSteps: {
    s1body: "We run a broader enterprise onboarding — your structure, your existing legal/compliance docs, your team's pain points — and surface where the company is exposed. (Smaller projects can also start with a self-managed review at complisunguardian.com.)",
    s2body: "Our experts build an intuitive prototype configured to your branding, policies, calendar, and team in 14 days. You walk through it with us.",
    s3body: "We refine the prototype together until it fits — then go live. The fractional team stays alongside you and scales up or down with the business — not a hand-off.",
    p2body: "We build the prototype in 14 days, you review with our team, we refine until it matches how you actually work.",
    p3body: "Refinements baked in; you go live; fractional team scales up or down with your business — ongoing partnership, not a hand-off.",
    timelineCallout: "$5,000 installation covers all 45 days of prototyping, configuration, and go-live support",
  },
  proofKpi: "Scoped · budgeted · SLA-bound — every matter",
  pricingAnchor: { mo: 1500, install: 5000, plusFractional: 3225 },
};

// ---------- 2A — Big Law Firm Channel (Dykema, Chapman, Mavacy) ----------
const A_2A = {
  kind: "2A",
  label: "Law Firm · Operations Platform",
  lawFirm: true,
  suppressTryIt: true,
  suppressTrusted: true,

  // S01 — Title
  titleHeadline: {
    lead: "Your clients are already using AI.",
    italic: "Your attorneys are too.",
    trail: "Let's make that safe — and turn it into a competitive advantage.",
  },
  titleSubline: "Your clients are pasting contracts into ChatGPT and asking for redlines. Your associates are using AI notetakers in client meetings without telling anyone. CompliSun gives you a protected environment so you can tell clients — confidently — exactly how your firm's AI use is structured and protected, and make your whole practice radically transparent for them.",
  titleStats: [
    { num: "83%", label: "of clients say firm tech sophistication affects their confidence", source: "Integris 2026", href: "https://integrisit.com/blog/how-law-firm-technology-adoption-is-shaping-client-trust-in-2026/" },
    { num: "78%", label: "of clients want lawyers to disclose AI use · 35% of lawyers rarely or never do", source: "Clio 2025", href: "https://www.clio.com/resources/legal-trends/" },
    { num: "50%", label: "of clients want greater billing transparency · only 62% of firms have done anything about it", source: "BigHand 2023 · Best Law Firms 2025", href: "https://www.bestlawfirms.com/articles/law-firms-embrace-afas-but-clients-want-more-flexibility/7098" },
  ],
  titlePricingPills: [
    { eyebrow: "Law firm platform",   value: "$59 / user / mo + $5,000 install" },
    { eyebrow: "Founding firm",       value: "20% off both — first 12 months" },
  ],
  titlePricingFootnote: null,
  titleBadge: "Built for law firms that want to lead on technology, not react to it",

  // S02 — Problem
  problemHeadline: {
    lead: "Your attorneys don't need",
    italic: "another tool.",
    trail: "They need one that actually fits how lawyers work.",
  },
  problemBody1: "Matter files scattered across DMS, Outlook, Teams chats, and personal drives. Associates running AI in the shadows because the firm hasn't sanctioned anything. Clients calling for updates that should already be visible to them. Notetakers recording privileged conversations with no controls on where the transcript ends up. The problem isn't a shortage of legal tech — it's that none of it was built for the way attorneys, paralegals, and clients actually need to work together.",
  suppressProblemQuote: true,
  problemCards: [
    { title: "Shadow AI is already inside your firm",      body: "Associates are using ChatGPT, Copilot, and consumer AI tools on client work. The privilege risk is real, and pretending it isn't won't make it stop." },
    { title: "Clients are flying blind between updates",   body: "They shouldn't have to email an assistant to find out what's happening on their matter. Status, documents, and messages should be one click away." },
    { title: "Meeting notetakers are a privilege landmine", body: "When an AI notetaker joins a privileged call, where does the transcript live? Who can see it? Is it work product? Most firms can't answer that question." },
    { title: "Institutional knowledge is locked in heads",  body: "A senior partner's expertise should compound across the firm — not walk out the door when they retire. The firm's collective intelligence needs a home." },
  ],

  // S05 — Guide
  guidePromise: "We've worked inside law firms and inside the fintechs that hire them. We know exactly where law firm operations break down — and where small improvements build outsized confidence with clients. When clients can log in, see what you've done, read the research you've cleared for them, and reach you instantly, they don't just stay — they refer.",
  guidePromise2: "We build technology for law firms that turns operational discipline into a client-facing advantage.",
  guideCards: [
    { title: "Inside-the-firm experience",      body: "Our team has worked at AmLaw firms and inside legal departments. We know the workflows, the friction, and the unwritten rules." },
    { title: "Fintech-grade engineering",       body: "Built on AWS with the same security architecture that protects regulated financial institutions. SOC 2-grade isolation, end-to-end." },
    { title: "Privilege-aware by design",       body: "Every AI output, every notetaker transcript, every document — taggable as Attorney-Client Privileged or Attorney Work Product, with audit trails." },
    { title: "Clients want this too",           body: "The same platform that runs your operations is the platform your clients log into. One system, two views, zero confusion." },
  ],
  colinTitle: "Founder · Attorney · Builder",
  colinBio: "Former General Counsel and Chief Compliance Officer at Rocket. Michigan attorney. Has been on both sides of the outside-counsel relationship — and built CompliSun's law firm platform to solve the things he hated most about both.",

  // S06b — How It Works
  howItWorksSteps: {
    s1body: "Broader enterprise onboarding — we review your matter intake, current DMS, AI policy, and client communication workflows. Privilege protocols documented up front.",
    s2body: "Our team builds a working prototype configured to your firm in 14 days — your branding, your matter types, your privilege protocols, your client portal.",
    s3body: "We refine the prototype with your attorneys, paralegals, and IT team until it fits how the firm actually works. Then go live — and the team stays alongside, scaling support up or down as the firm needs it.",
    p1body: "Onboarding form, workflow review, matter types mapped, privilege protocols documented.",
    p2body: "We build the prototype in 14 days. Your team walks through it with ours. We refine until it matches how your attorneys actually practice.",
    p3body: "Refinements baked in; client portal configured; firm goes live; team stays alongside and scales up or down as the firm needs.",
    timelineCallout: "$5,000 installation covers all 45 days of prototyping, configuration, and go-live support — 20% off for Founding Firms",
  },

  // S07 — Closed Systems
  closedSystemsIntro: "Open AI systems learn from everything you give them — and that data often lives on someone else's infrastructure, available to train someone else's model. For a law firm, that's not a risk tolerance question. That's a privilege question. A 'closed system' means your client data, your work product, and your firm's collective knowledge stay inside your tenant, on infrastructure you control, used only for you.",
  closedSystemsBullets: [
    "Client-privileged work product stays in YOUR tenant — never in a vendor's training set.",
    "Attorney-Client Privileged and Attorney Work Product designations applied at the document, message, and AI-output level.",
    "Role-based access at every level: partner, associate, paralegal, client — everyone sees only what their role permits.",
    "Meeting notetaker output captured directly into the protected environment with privilege flags applied automatically where the matter requires it.",
    "Privilege preserved by architecture, not just policy.",
  ],

  // S11c — Platform (law-firm modules replace the standard tabs)
  platformBody: "Two views, one platform. Your attorneys, paralegals, and staff get an internal workspace built around matters, clients, and firm knowledge. Your clients get a portal that makes them feel like they have the most attentive law firm in the country — because they do.",
  platformFooter: "Want to see it live? We can give your firm sandbox access today, or start building your prototype.",
  lawFirmPlatformTabs: ["attorney-dashboard", "complimind", "client-news", "client-portal", "notetaker", "law-prototype"],

  // S12 — The Offer (replaces "CompliSun Legal PLC" slide for 2A)
  offerHeadline: "An operations system your firm runs on. A client experience your clients want to visit.",
  offerIntro: "This is the offer for law firms ready to lead on technology. One platform — two experiences — built so your operations take care of themselves and your clients have a reason to log in every week.",
  offerFirmFeatures: [
    "Matter management with privilege-aware AI summaries",
    "CompliMind: firm-trained AI assistant team (drafting, research, summarization)",
    "Notetaker controls — choose which meetings AI can join; auto-flag privilege and work product",
    "Client news + negative news monitoring across your entire book",
    "Document and template library — firm knowledge that compounds",
    "Role-based access: partners, associates, paralegals, staff",
    "AWS-hosted, SOC 2-grade tenant isolation",
    "Operations that take care of themselves",
  ],
  offerClientFeatures: [
    "Client portal branded as your firm",
    "Real-time secure chat with their attorney team",
    "Matter status, deadlines, and document access — always visible",
    "Webinar hosting — client-only events you post inside the portal",
    "Curated research and updates you've cleared for client consumption",
    "Attorney-Client Privilege and Work Product designations clearly flagged",
    "The kind of transparency that gets you referred",
  ],
  offerStickiness: "When clients have a reason to log in every week — webinars, documents, research, status — they don't shop your work. They refer it.",

  // S15 — Contact / Build my Prototype
  contactPricing: { perUser: 59, install: 5000, foundingDiscount: 0.20 },

  // Existing fields (kept for back-compat with non-touched code paths)
  platformTabs: ["dashboard", "monitoring", "vendor", "training"],
  tryItModules: ["vendor", "marketing", "hotline", "training"],
  proofKpi: "Add a competitive technology line in 45 days",
  pricingAnchor: { perUser: 59, install: 5000, perTenant: true },
};

// ---------- 2B — Solo Attorney / Consultant Channel ----------
const A_2B = {
  kind: "2B",
  label: "Solo Channel · CMS Productized",
  problemCards: [
    { title: "You build CMS from scratch — every time",         body: "Same architecture, different brand. 15 years of CRCM expertise spent on assembly, not judgment." },
    { title: "Throughput capped at your personal bandwidth",   body: "Your clients pay for both your expertise AND your time — and your time is the bottleneck." },
    COMMON_AI,
    { title: "You should sell expertise, not assembly",         body: "The framework is the same every time. Productize it. Spend your hours on judgment calls and regulator-facing relationships." },
  ],
  guidePromise: "We built the platform you'd build from scratch. You keep the CRCM expertise, the bank-partner relationships, the client revenue. We provide the infrastructure layer.",
  closedSystemsBullets: [
    "Each client has their own tenant. Your access lets you administer across the book; clients see only their own program.",
    "Privilege protections appropriate to your legal services engagement.",
    "White-labeled to YOUR brand — clients log in to a portal you control.",
    "Unlimited client tenants under one platform instance (up to 8 active — volume trigger after).",
  ],
  platformTabs: ["dashboard", "monitoring", "vendor", "hotline", "training"],
  tryItModules: ["vendor", "marketing", "hotline", "training"],
  proofKpi: "Productize CMS · unlock throughput",
  pricingAnchor: { mo: 1500, volumeTriggerAt: 8, escalatedMo: 2500 },
};

// ---------- 3 — Bank Channel Partner ----------
const A_3 = {
  kind: "3",
  label: "Bank Channel · Sponsor-Bank Partner Program",
  titleSubline: "we built CompliSun to be the continuous-monitoring layer between you and your partner book. Same risk methodology, same evidence cadence, applied to every partner from onboarding through ongoing oversight — and a fractional team that handles the work alongside it.",
  problemCards: [
    { title: "Partner onboarding is a custom build every time",       body: "Each new fintech partner is a fresh diligence package — KYB, control attestations, vendor stack review, product-risk scoring. Today your team rebuilds the questionnaire and chases evidence partner-by-partner." },
    { title: "Continuous monitoring isn't really continuous",          body: "OFAC re-screening, complaint-volume drift, third-party-platform changes, AML transaction patterns — these need to be watched in flight, not surfaced at the quarterly review. Today they're mostly surfaced when something breaks." },
    { title: "Partners' programs vary — your standard can't",          body: "Some partners have mature compliance functions. Most don't. Your TPRM standard has to flex without bending. Examiners want consistency; partners want speed; you're stuck refereeing." },
    { title: "Real-time partner visibility, not quarterly questionnaires", body: "Your oversight team should see partner programs as they evolve — control test results, escalations, transaction anomalies, training completion — not get surprised at the next exam." },
  ],
  guidePromise: "A continuous-monitoring layer for your fintech partner book — one platform that runs onboarding diligence, then keeps assessing partner programs and platforms in flight. White-labeled per partner, cross-tenant read-only for you, with a fractional team that runs the assessments alongside it.",
  closedSystemsIntro: "Built specifically for sponsor banks running fintech partner programs: each partner has an isolated tenant under your master oversight view. You see everything. Partners see only their own.",
  closedSystemsBullets: [
    "Each partner has its own isolated tenant. Bank has cross-tenant read-only access for oversight — partners cannot see each other's data.",
    "Continuous third-party monitoring: OFAC re-screening, control-attestation drift, complaint-volume trends, AML pattern checks — flowing into your oversight view in flight.",
    "AWS Bedrock + Anthropic architecture. Examiner-ready audit trail per partner.",
    "Bank-facing deliverables — partner risk assessments, monitoring exception reports, exam-readiness packs — produced on YOUR cadence by the fractional team.",
  ],
  platformTabs: ["dashboard", "monitoring", "vendor", "training"],
  tryItModules: ["partner-monitoring", "vendor", "marketing", "training"],
  tryItLabels: {
    vendor:    { label: "Partner onboarding", sub: "Fintech-partner diligence pack" },
    marketing: { label: "Partner marketing review", sub: "Reg-Z · UDAAP · FTC review of partner promos" },
    training:  { label: "Partner training",  sub: "Per-tenant attestation + completion" },
  },
  tryItSubhead: "Click into the partner-monitoring view first — that's your cross-tenant oversight layer. The other three are the per-partner workflows that feed it: onboarding diligence, partner marketing review, partner training & attestation.",
  proofKpi: "Standardize once · monitor continuously · apply across the book",
  pricingAnchor: null, // held back for term-sheet
};

// ---------- 4 — Member Association ----------
const A_4 = {
  kind: "4",
  label: "Member Association · Member Benefit Program",
  problemCards: [
    { title: "Members face regional-bank pressure with community-bank teams", body: "Same examiners, 1-2 compliance FTEs. Hiring isn't the answer — productized infrastructure is." },
    { title: "Each member has a different regulator",          body: "State-by-state rules, sponsor-bank attestations, MLA/SCRA layers — too many threads for one inbox." },
    COMMON_AI,
    { title: "Your dues need to justify themselves",            body: "A platform members USE every week (not just read newsletters from) is the strongest retention lever you have." },
  ],
  guidePromise: "Member-discounted access to the full CompliSun service suite — pre-configured for your member profile. Single point of program management for members without the headcount.",
  closedSystemsBullets: [
    "Each member bank has its own isolated tenant. Association does NOT see member data — by design.",
    "Pre-configured templates: community-bank risk assessments (CBM); MLA/SCRA controls (AMBA).",
    "Member-only content channel: webinars, regulatory update briefings, peer benchmarking.",
    "Role-based access at every level: association staff, member admin, member team.",
  ],
  platformTabs: ["dashboard", "monitoring", "vendor", "training"],
  tryItModules: ["vendor", "marketing", "training"],
  proofKpi: "Retention lift from week-one member usage",
  pricingAnchor: { mo: 3225, install: 5000, memberDiscount: true },
};

// ---------- 6 — Real Estate Fintech Adjacent (combined platform + legal) ----------
const A_6 = {
  kind: "6",
  label: "Real Estate Fintech · Combined Platform + Legal",
  problemCards: [
    { title: "Spend management lives in a regulatory grey zone", body: "The bigger you get, the darker the zone — sponsor banks sharpen, regulators notice." },
    { title: "Larger customers ask for SOC 2 + BSA/AML evidence", body: "REITs and institutional operators want OFAC screening logs and complaint-management procedures before they sign." },
    { title: "Money transmitter posture is a state-by-state map", body: "Investors will ask. Sponsor banks already do. You need an answer that holds up." },
    { title: "Your team gets pulled away from product",        body: "Every partner-bank question, every customer-due-diligence ask — that's time NOT spent winning." },
  ],
  guidePromise: "CompliSun + CompliSun Legal as ONE combined engagement. BaaS depth, money-services regulatory experience, and a platform that produces evidence your sponsor bank and biggest customers will actually ask for.",
  closedSystemsBullets: [
    "Multi-tenant isolation. RBAC. AWS Bedrock + Anthropic — same as our regulated-fintech clients.",
    "SOC 2 Type II readiness baked into the platform architecture from day one.",
    "Real-estate-specific layer: trust accounting reviews, vendor 1099 workflow design, lien-interaction issue spotting.",
    "Privilege preserved on the legal side — work product stays in your tenant.",
  ],
  platformTabs: ["dashboard", "monitoring", "vendor", "training"],
  tryItModules: ["vendor", "marketing", "training"],
  proofKpi: "60-day rollout · sponsor-bank-ready evidence",
  pricingAnchor: { mo: 3225, install: 5000, plusLegal: true },
};

const PACKS = {
  "1A": A_1A,
  "1B": A_1B,
  "2A": A_2A,
  "2B": A_2B,
  "3":  A_3,
  "4":  A_4,
  "6":  A_6,
};
